import { useQuery } from '@tanstack/react-query';
import { IGame, IPlayer } from '~/types/api';
import { INews, IPage } from '~/components/layouts/Search';

interface SearchResults {
  players: IPlayer[];
  games: IGame[];
  news: INews[];
  pages: IPage[];
}

/**
 * Custom hook for fetching search results using React Query
 * @param searchTerm The search term to query
 * @param contentFilter Optional filter for specific content types
 * @returns Query result containing search data and loading state
 */
export function useSearch(searchTerm: string | null, contentFilter?: string) {
  return useQuery<SearchResults>({
    queryKey: ['search', searchTerm, contentFilter],
    queryFn: async () => {
      if (!searchTerm) {
        return { players: [], games: [], news: [], pages: [] };
      }
      
      const params = new URLSearchParams();
      params.set('q', searchTerm);
      if (contentFilter) {
        params.set('type', contentFilter);
      }
      
      const response = await fetch(`/api/search?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error('Search failed');
      }
      
      return await response.json();
    },
    // Only run query if we have a search term
    enabled: !!searchTerm,
    // Keep the data fresh for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Retry failed requests 3 times
    retry: 3,
    // Show stale data while refetching
    refetchOnWindowFocus: false
  });
} 